import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PromoValidation {
  code: string;
  valid: boolean;
  reason: string;
  discount_type: "percent" | "amount" | null;
  discount_value: number | null;
  duration: "once" | "repeating" | "forever" | null;
  duration_months: number | null;
  applies_to_tiers: string[] | null;
  influencer_handle: string | null;
  attributed?: { attributed: boolean; reason: string; influencer_handle: string | null } | null;
}

const REASON_COPY: Record<string, string> = {
  not_found: "We don't recognize that code.",
  inactive: "That code is no longer active.",
  expired: "That code has expired.",
  exhausted: "That code has been fully redeemed.",
  ok: "Code applied.",
};

export const usePromoCode = () => {
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState<PromoValidation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apply = useCallback(
    async (raw: string, opts: { attribute?: boolean; context?: "signup" | "checkout" } = {}) => {
      const code = raw.trim().toUpperCase();
      if (!code) return null;
      setBusy(true);
      setError(null);
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke<PromoValidation & { error?: string }>(
          "apply-promo-code",
          { body: { code, attribute: opts.attribute, context: opts.context } },
        );
        if (invokeErr) throw new Error(invokeErr.message);
        if (data && "error" in data && data.error) throw new Error(String(data.error));
        if (!data) throw new Error("No response");
        setApplied(data);
        if (!data.valid) setError(REASON_COPY[data.reason] ?? "Couldn't apply that code.");
        return data;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Couldn't apply that code.");
        setApplied(null);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setApplied(null);
    setError(null);
  }, []);

  return { apply, clear, applied, busy, error };
};

export const formatDiscount = (p: PromoValidation): string => {
  if (!p.valid || !p.discount_type || p.discount_value == null) return "";
  if (p.discount_type === "percent") return `${p.discount_value}% off`;
  return `$${(p.discount_value / 100).toFixed(2)} off`;
};
